import React, { useState } from 'react'
import { InputField } from 'Components/reusable/InputFields/InputField'
import { IParsedNamed } from 'types/cvParserInterfaces'
import { parsedName, splitNames, withParsedNames } from 'utils/parsedProfileFields'

/** @notExported */
interface IParsedNamesFieldProps {
  /** Label, e.g. "Roles" */
  label: string
  /** Roles or industries of a project or employer */
  items: IParsedNamed[]
  /** Called with the edited roles or industries */
  onChange: (items: IParsedNamed[]) => void
  /** Language the names are shown and edited in */
  languageId: number
  /** Edit as comma-separated names instead of showing them */
  editing: boolean
  /** Test id of the input */
  testId?: string
}

/**
 * The names as a comma-separated text field. Mounted only while editing, so the text starts from the
 * current names each time; it keeps its own text, so a trailing comma isn't dropped before the next
 * name is typed.
 *
 * @returns The field
 * @notExported
 */
const NamesInput: React.FC<Omit<IParsedNamesFieldProps, 'editing'>> = ({
  label,
  items,
  onChange,
  languageId,
  testId,
}) => {
  const [text, setText] = useState(() => items.map(item => parsedName(item, languageId)).join(', '))

  return (
    <InputField
      label={label}
      value={text}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        setText(e.target.value)
        onChange(withParsedNames(items, splitNames(e.target.value), languageId))
      }}
      fullWidth
      insetLabel
      data-testid={testId}
    />
  )
}

/**
 * Roles or industries of a parsed project or employer, edited as comma-separated names
 *
 * @returns The names, or a field to edit them
 * @notExported
 */
const ParsedNamesField: React.FC<IParsedNamesFieldProps> = ({ editing, ...props }) => {
  if (editing) {
    return <NamesInput {...props} />
  }

  const names = props.items.map(item => parsedName(item, props.languageId)).filter(Boolean)
  return names.length > 0 ? <div>{`${props.label}: ${names.join(', ')}`}</div> : null
}

export default ParsedNamesField
